import { Injectable, OnDestroy } from '@angular/core';
import { catchError, debounceTime, map, Observable, of, retry, RetryConfig, Subject, take, takeUntil } from 'rxjs';

import { ApiUsersService, UpdateUserApplicationPreferencesDto } from '@shure/cloud/shared/users/data-access';
import { ILogger } from '@shure/shared/angular/utils/logging';

interface ComponentPreference<Payload> {
	key: string;
	payload: Payload;
}

/**
 * A Service to save and load component specific preferences in the BE. This service is
 * intended to be provided on a per-component basis.  Ideally each component would have a single
 * key/preferences mapping, but it does support > 1 mapping if necessary. Due to the use of
 * Generics, a single service instance will handle one type of preferences data.
 *
 * A component should load the preferences once when instantiated to fetch the latest values
 * from the BE, and then save preferences as necessary.
 *
 * All save's use a 2sec debouceTime to limit how frequently data is saved to on the server.
 * Note: if the the save operation is invokved and the instantiating component is destroyed before
 * the 2sec debounceTime elapses, the changes are saved immediately.
 */
@Injectable({
	providedIn: 'root'
})
export class UserComponentPreferencesService<PreferencesPayload> implements OnDestroy {
	private readonly keyPreferenceSubjectMap = new Map<string, Subject<ComponentPreference<PreferencesPayload>>>();
	private readonly destroy = new Subject<void>();
	private readonly retryConfig: RetryConfig = {
		count: 1,
		delay: 100
	};

	constructor(
		private readonly apiUsersService: ApiUsersService,
		private readonly logger: ILogger
	) {}

	public ngOnDestroy(): void {
		this.destroy.next();
		this.destroy.complete();
		this.keyPreferenceSubjectMap.clear();
	}

	/**
	 *
	 * @param key a component and app-specific key that uniquely identifies a set of preferences
	 * @param defaultPreferences - the preferences returned if no preferences are found on the server,
	 * or there was an issue retrieving them, or a validator was supplied and the preferences are not valid.
	 * @param preferencesValidator - an optional validator that will be invoked to validate the preferences
	 * Should return true if the data complies to the schema, and false otherwise.  Not necessary if you
	 * really trust the values returned from the BE.
	 * @returns
	 */
	public loadPreferences(
		key: string,
		defaultPreferences: PreferencesPayload,
		preferencesValidator?: (data: unknown) => boolean
	): Observable<PreferencesPayload> {
		return this.apiUsersService
			.getUserApplicationPreferences$Response({ componentIds: this.makeComponentId(key) })
			.pipe(
				takeUntil(this.destroy),
				take(1),
				retry(this.retryConfig),
				map((response) => {
					const body = response.body.body;
					if (body && Array.isArray(body) && body[0] !== undefined && body[0].configuration !== undefined) {
						if (preferencesValidator !== undefined && !preferencesValidator(body[0].configuration)) {
							throw new Error('preferences validator failed');
						}
						const preferences = <PreferencesPayload>body[0].configuration;
						return preferences;
					}
					return defaultPreferences;
				}),
				catchError((error: unknown) => {
					this.logger.error('loadPreferences', 'error loading preferences: %o', error);
					return of(defaultPreferences);
				})
			);
	}

	/**
	 * Save the given key/prefernces data to the BE after a 2sec debounceTime.
	 * @param key a component and app-specific key that uniquely identifies a set of preferences
	 * @param preferences - the preferences to save.
	 */
	public updatePreferences(key: string, preferences: PreferencesPayload): void {
		let subject = this.keyPreferenceSubjectMap.get(key);
		if (subject === undefined) {
			subject = new Subject<ComponentPreference<PreferencesPayload>>();
			this.keyPreferenceSubjectMap.set(key, subject);
			subject.pipe(takeUntil(this.destroy), debounceTime(2_000)).subscribe({
				next: (newPreferences) => this.save(newPreferences.key, newPreferences.payload)
			});
		}
		subject.next({ key, payload: preferences });
	}

	private save<PreferencesPayload>(key: string, payload: PreferencesPayload): void {
		const body: UpdateUserApplicationPreferencesDto = {
			componentId: this.makeComponentId(key),
			configuration: <object>payload
		};

		this.apiUsersService
			.updateUserApplicationPreferences$Response({ body })
			.pipe(takeUntil(this.destroy), take(1), retry(this.retryConfig))
			.subscribe({
				error: (error: unknown) =>
					this.logger.error('save', 'Error saving component preferences: %o', { body, error })
			});
	}

	private makeComponentId(key: string): string {
		return `shure-cloud-component-${key}`;
	}
}
